/**
 * The client's copy of his due follow-ups, and the ask that fires once a
 * datable thing he mentioned in chat has come to pass.
 *
 * The server owns the follow-ups (`glitime_memories`, kind='upcoming',
 * extracted from chat — see `glitime-memory.ts`); this module caches the due
 * ones per account in Dexie meta, mirroring `promises.ts` exactly: refreshed
 * on boot and after every chat close, so the question can surface with the
 * phone in airplane mode. Where a broken promise is a roast, a due follow-up
 * is the caring twin of the same machinery — the same cache shape, the same
 * fire-and-forget resolve, the same local guard against asking twice.
 *
 * The guard matters more here than for promises: a follow-up can be offered
 * from two different call sites in the same session — the chat opener and
 * the daily greeting — and she must ask "Kaip egzaminas?" exactly once
 * between them, not once each. Because both read the same module-level
 * cache and the same guard, whichever site asks first is the one that gets
 * to.
 */

import { api } from '../api/client.js'
import { getMeta, setMeta } from '../db/dexie.js'
import { currentProfile } from '../screens/useProfile.js'

export interface CachedFollowUp {
  id: string
  text: string
  ask: string
  dueOn: string
  /** 'active': never surfaced yet. 'asked': already offered once, server-side. */
  status: 'active' | 'asked'
}

const ANON_OWNER = 'anon'

function ownerKey(): string {
  return currentProfile()?.id ?? ANON_OWNER
}

function metaKey(owner: string): string {
  return `glitime.followups.${owner}`
}
function askedKey(owner: string): string {
  return `glitime.followupAsked.${owner}`
}

const cache = new Map<string, CachedFollowUp[]>()
const asked = new Map<string, string[]>()
const hydrationPromises = new Map<string, Promise<void>>()

function hydrate(owner: string): Promise<void> {
  let promise = hydrationPromises.get(owner)
  if (promise == null) {
    promise = Promise.all([
      getMeta<CachedFollowUp[]>(metaKey(owner), []),
      getMeta<string[]>(askedKey(owner), []),
    ]).then(([followUps, askedIds]) => {
      if (!cache.has(owner)) cache.set(owner, followUps)
      if (!asked.has(owner)) asked.set(owner, askedIds)
    })
    hydrationPromises.set(owner, promise)
  }
  return promise
}

/** Pull the server's current due set into the cache. Fire-and-forget friendly. */
export async function refreshFollowUps(): Promise<void> {
  const owner = ownerKey()
  if (owner === ANON_OWNER) return
  try {
    const wire = await api.get<{ followUps: CachedFollowUp[] }>('/api/ai/followups')
    cache.set(owner, wire.followUps)
    await setMeta(metaKey(owner), wire.followUps)
    // Ids the server no longer lists have been resolved (asked and answered,
    // or lapsed) — the guard can shrink.
    const live = new Set(wire.followUps.map((f) => f.id))
    const kept = (asked.get(owner) ?? []).filter((id) => live.has(id))
    asked.set(owner, kept)
    await setMeta(askedKey(owner), kept)
  } catch {
    // Offline or signed out: the cached copy keeps working, which is the point.
  }
}

/**
 * The first cached follow-up that is due, never yet asked server-side, and
 * not locally guarded as already offered this session — or null.
 */
export function dueFollowUp(): { id: string; ask: string } | null {
  const owner = ownerKey()
  void hydrate(owner)
  const followUps = cache.get(owner) ?? []
  if (followUps.length === 0) return null
  const askedIds = asked.get(owner) ?? []
  const hit = followUps.find((f) => f.status === 'active' && !askedIds.includes(f.id))
  return hit == null ? null : { id: hit.id, ask: hit.ask }
}

/**
 * `dueFollowUp()`, plus the side effects of actually asking it: the local
 * guard is set first (so a second call site in the same session — chat
 * opener, then daily greeting, or the other way round — never asks it
 * again), the cache is optimistically flipped to 'asked', and the server is
 * told, fire-and-forget, exactly like `maybePromiseToast`'s resolve.
 */
export function takeFollowUpAsk(): { id: string; ask: string } | null {
  const followUp = dueFollowUp()
  if (followUp == null) return null

  const owner = ownerKey()
  // Guard first, resolve second: the ask has already been shown, and a
  // second device repeating it because this request is still in flight
  // would be worse than a resolve that arrives twice (the server takes the
  // first).
  const askedIds = [...(asked.get(owner) ?? []), followUp.id]
  asked.set(owner, askedIds)
  void setMeta(askedKey(owner), askedIds)
  cache.set(
    owner,
    (cache.get(owner) ?? []).map((f) =>
      f.id === followUp.id ? { ...f, status: 'asked' as const } : f,
    ),
  )
  void setMeta(metaKey(owner), cache.get(owner) ?? [])
  void api.post(`/api/ai/followups/${followUp.id}/resolve`, { status: 'asked' }).catch(() => {
    // Offline: the local guard holds; the server learns on the next chat
    // close, which already resolves any 'asked' row to 'done'.
  })
  return followUp
}

/**
 * Explicit resolution from a UI dismiss action ("Jau papasakojau") rather
 * than from actually asking — drops it from the cache and tells the server,
 * fire-and-forget, same as above.
 */
export function resolveFollowUpDone(id: string): void {
  const owner = ownerKey()
  void hydrate(owner)
  const askedIds = [...(asked.get(owner) ?? []), id]
  asked.set(owner, askedIds)
  void setMeta(askedKey(owner), askedIds)
  cache.set(
    owner,
    (cache.get(owner) ?? []).filter((f) => f.id !== id),
  )
  void setMeta(metaKey(owner), cache.get(owner) ?? [])
  void api.post(`/api/ai/followups/${id}/resolve`, { status: 'done' }).catch(() => {
    // Offline: the cache already dropped it; the server learns next refresh.
  })
}

/** Test-only: wait for an account's Dexie-backed follow-up cache to load. */
export function __hydrateFollowUpsForTests(owner: string): Promise<void> {
  return hydrate(owner)
}
/** Test-only: drop the caches so the next read starts cold. */
export function __resetFollowUpsForTests(): void {
  cache.clear()
  asked.clear()
  hydrationPromises.clear()
}
